import { useEffect, useMemo, useState } from "react";

/**
 * Client-side pagination over an already-filtered array.
 *
 * Use this when:
 *   • The backend endpoint returns the full list in one shot, and
 *   • The page does post-fetch filtering (search box / status pill / etc.)
 *     so we cannot safely move filters to the server.
 *
 * Usage:
 *   const { paged, page, pageSize, total, setPage, setPageSize } =
 *     useClientPagination(filtered, { initialPageSize: 50 });
 *
 *   <PaginationControls page={page} pageSize={pageSize} total={total}
 *     onPage={setPage} onPageSize={setPageSize}
 *     pageSizeOptions={[25, 50, 100, 200]} />
 *
 * Auto-resets to page 1 when the filtered length OR pageSize changes.
 */
export function useClientPagination(items, opts = {}) {
  const { initialPageSize = 50 } = opts;
  const arr = Array.isArray(items) ? items : [];
  const total = arr.length;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Snap page into bounds whenever total or pageSize changes
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
    if (page < 1) setPage(1);
  }, [pageCount, page]);

  // Reset to page 1 when the filter result set or pageSize meaningfully changes
  useEffect(() => {
    setPage(1);
  }, [total, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return arr.slice(start, start + pageSize);
  }, [arr, page, pageSize]);

  return {
    paged,
    page,
    pageSize,
    total,
    pageCount,
    setPage,
    setPageSize: (n) => setPageSize(Number(n) || initialPageSize),
  };
}

export default useClientPagination;
